import { Children, isValidElement } from 'react';
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { FilterBar, FilterField } from '../data/FilterBar';
import { cn } from './utils';

type CardProps = HTMLAttributes<HTMLElement> & {
    as?: 'article' | 'section' | 'div';
    children: ReactNode;
};

function Card({ as: Comp = 'article', className, children, ...props }: CardProps) {
    return (
        <Comp className={cn('nx-card', className)} {...props}>
            {children}
        </Comp>
    );
}

type CardHeadProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
    title?: ReactNode;
    children?: ReactNode;
    actions?: ReactNode;
};

function CardHead({ title, children, actions, className, ...props }: CardHeadProps) {
    const filterActions = getFilterActions(actions);

    if (filterActions) {
        return (
            <>
                <header className={cn('nx-card-head', className)} {...props}>
                    {title != null ? <h2>{title}</h2> : children}
                </header>
                <FilterBar>{filterActions}</FilterBar>
            </>
        );
    }

    return (
        <header className={cn('nx-card-head', className)} {...props}>
            {title != null ? <h2>{title}</h2> : children}
            {actions}
        </header>
    );
}

/** Alias semântico para painéis do Dashboard / listagens */
function PanelCard({ className, ...props }: CardProps) {
    return <Card className={cn(className)} {...props} />;
}

export { Card, CardHead, PanelCard };
export type { CardProps, CardHeadProps };

type ElementWithProps = ReactElement<{
    className?: string;
    children?: ReactNode;
    filter?: boolean;
    title?: string;
    type?: string;
}>;

function getFilterActions(actions: ReactNode) {
    if (!isValidElement(actions)) {
        return null;
    }

    const element = actions as ElementWithProps;
    if (!hasClassName(element, 'nx-card-head-actions')) {
        return null;
    }

    if (hasClassName(element, 'nx-listing-toolbar')) {
        return null;
    }

    return normalizeFilterChildren(element.props.children);
}

function normalizeFilterChildren(children: ReactNode): ReactNode {
    return Children.map(children, (child) => {
        if (!isValidElement(child)) {
            return child;
        }

        const element = child as ElementWithProps;

        if (hasClassName(element, 'nx-search-field')) {
            return (
                <div className="nx-filters-search">
                    <label className="nx-filter-field nx-filter-field--search">
                        <span>{inferSearchLabel(element)}</span>
                        {element}
                    </label>
                </div>
            );
        }

        if (element.props.filter || element.props.title || element.props.type === 'date') {
            return <FilterField label={inferFilterLabel(element)}>{element}</FilterField>;
        }

        return element;
    });
}

function hasClassName(element: ElementWithProps, className: string) {
    return typeof element.props.className === 'string'
        && element.props.className.split(/\s+/).includes(className);
}

function inferSearchLabel(element: ElementWithProps) {
    const text = findTextProp(element.props.children, 'placeholder').toLowerCase();
    return text.includes('motorista') ? 'Motorista' : 'Buscar';
}

function inferFilterLabel(element: ElementWithProps) {
    const title = element.props.title;
    const option = findFirstOptionText(element.props.children);
    const text = String(title || option || '').toLowerCase();

    if (text.includes('status') || text === 'todos') return 'Status';
    if (text.includes('tipo')) return 'Tipo';
    if (text.includes('resultado')) return 'Resultado';
    if (text.includes('veiculo') || text.includes('veículo')) return 'Veículo';
    if (text.includes('varia')) return 'Variação';
    if (text.includes('ordem') || text.includes('op')) return 'Ordem de Produção';
    if (text.includes('inicio') || text.includes('início')) return 'Data início';
    if (text.includes('fim')) return 'Data fim';

    return 'Filtro';
}

function findTextProp(children: ReactNode, propName: 'placeholder') {
    let value = '';

    Children.forEach(children, (child) => {
        if (value || !isValidElement(child)) return;
        const element = child as ReactElement<Record<string, unknown>>;
        const prop = element.props[propName];
        if (typeof prop === 'string') {
            value = prop;
        }
    });

    return value;
}

function findFirstOptionText(children: ReactNode) {
    let value = '';

    Children.forEach(children, (child) => {
        if (value || !isValidElement(child)) return;
        const element = child as ReactElement<{ children?: ReactNode }>;
        const content = element.props.children;
        if (typeof content === 'string') {
            value = content.trim();
        }
    });

    return value;
}
